import { bodyCopyLink, Button, CounterPill, Icon, Pullout, PulloutType } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import ParseHtml from 'html-react-parser';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { DistributionType, GroupType } from '../../models';
import { IHierarchyReportingAccessConfig } from '../../models/IHierarchyReportingAccessConfig';
import { IReportingProfileConfig } from '../../models/IReportingProfileConfig';
import { IHierarchyReportingAccessState } from '../../store/HierarchyReportingAccessStore';
import { Col10Col2, ColFull, Label } from '../common';
import { ExpandableRowTable } from '../common/ExpandableTableRow';
import { HierarchyReportingAccessConfigModalStoreWrapper } from './HierarchyReportingAccessConfigModal';
import { HierarchyReportingAccessConfigRow } from './HierarchyReportingAccessConfigRow';
import { HierarchyReportingAccessDeleteModal } from './HierarchyReportingAccessDeleteModal';

export const LabelTextWithCounter = styled.div`
	vertical-align: middle;
	display: inline-block;
`;
LabelTextWithCounter.displayName = 'LabelTextWithCounter';

export const DisabledRowContainer = styled.div`
	text-align: center;
	a {
		${bodyCopyLink}
	}
`;

export type HierarchyReportingAccessProps = IHierarchyReportingAccessState & {
	distributionType: DistributionType;
	isSurveyAdmin?: boolean;
	reportingProfileConfigs: IReportingProfileConfig[];
	surveyId: number;

	AddHierarchyReportingAccessConfig: (surveyId: number, model: IHierarchyReportingAccessConfig) => void;
	CloseDeleteConfigModal: () => void;
	CloseEditConfigModal: () => void;
	DeleteHierarchyReportingAccessConfig: (surveyId: number, model: IHierarchyReportingAccessConfig) => void;
	InitializeReportingProfileState: (surveyId: number, distributionType: DistributionType) => void;
	InitializeState: (surveyId: number, distributionType: DistributionType) => void;
	OpenDeleteConfigModal: (config?: IHierarchyReportingAccessConfig) => void;
	OpenEditConfigModal: (config?: IHierarchyReportingAccessConfig) => void;
	UpdateHierarchyReportingAccessConfig: (surveyId: number, config: IHierarchyReportingAccessConfig, closeModal: boolean) => void;
	UpdateOutOfSyncHierarchyReportingAccessConfigUserCount: (
		surveyId: number,
		hierarchyReportingAccessConfigId: number,
		userCount: number,
	) => void;
};

export class HierarchyReportingAccess extends React.PureComponent<HierarchyReportingAccessProps> {
	constructor(props: HierarchyReportingAccessProps) {
		super(props);
		this.AddHierarchyReportingAccessConfig = this.AddHierarchyReportingAccessConfig.bind(this);
		this.DeleteHierarchyReportingAccessConfig = this.DeleteHierarchyReportingAccessConfig.bind(this);
		this.UpdateHierarchyReportingAccessConfig = this.UpdateHierarchyReportingAccessConfig.bind(this);
	}

	private get Warnings() {
		return {
			...(() => {
				const invalidProfiles = this.props.reportingProfileConfigs.filter((c) => c.isOpenImmediately === false && c.openOn == null);

				if (invalidProfiles.length > 0) {
					return {
						'Invalid Profiles': ContentConstants.REPORTING_PROFILES_MESSAGE_INVALID_GO_LIVE_DATE.replace(
							'{InvalidProfileNames}',
							invalidProfiles.map((p) => p.name).join(', '),
						),
					};
				}
			})(),

			...(this.GroupAccessInfoNotAvailable && {
				'Group Access Info N/A': ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING,
			}),
		};
	}

	private get GroupAccessInfoNotAvailable(): boolean {
		const { fetchingGroupAccessOwners, hierarchies, profiles } = this.props;

		return fetchingGroupAccessOwners === 'N/A' || [hierarchies, profiles].some((dep) => !dep.isLoading && dep.couldNotFetchData);
	}

	public componentDidMount() {
		this.props.InitializeState(this.props.surveyId, this.props.distributionType);
		if (this.props.isSurveyAdmin) {
			this.props.InitializeReportingProfileState(this.props.surveyId, this.props.distributionType);
		}
	}

	public componentDidUpdate(previousProps: HierarchyReportingAccessProps) {
		// need to update the profile list after saving is complete.
		if (this.props.isSurveyAdmin && !this.props.saving && previousProps.saving) {
			this.props.InitializeReportingProfileState(this.props.surveyId, this.props.distributionType);
		}

		// for new out of sync config id that has been added, update stored value via the api
		this.props.outOfSyncHierarchyReportingAccessConfigId.forEach((id) => {
			if (previousProps.outOfSyncHierarchyReportingAccessConfigId.indexOf(id) === -1) {
				const config = this.props.hierarchyReportingAccessConfigs.find((c) => c.hierarchyReportingAccessConfigId === id)!;

				this.props.UpdateOutOfSyncHierarchyReportingAccessConfigUserCount(this.props.surveyId, id, config.userCount);
			}
		});
	}

	public render() {
		if (!this.props.showAddBulkRow) {
			return null;
		}

		if (!this.props.enableAddBulkRow) {
			if (this.props.isSurveyAdmin) {
				return null;
			}

			return (
				<>
					<ColFull id='tws-reporting-users-bulk'>
						<Label size='md'>{ContentConstants.SUBSECTIONHEADERS_REPORTING_REPORTING_USERS_BULK}</Label>
						<div className='row'>
							<DisabledRowContainer className='col-10 offset-md-1'>
								<p>
									<strong>{ContentConstants.REPORTINGUSER_SECTION_DISABLED_HEADER}</strong>
								</p>
								<p>{ParseHtml(ContentConstants.REPORTINGUSER_SECTION_DISABLED_BODY.replace('{audience_url}', 'audiences'))}</p>
							</DisabledRowContainer>
						</div>
					</ColFull>
				</>
			);
		}

		const { reportingProfileConfigs, hrisId } = this.props;
		const totalUsers = (reportingProfileConfigs ?? []).reduce((total, config) => total + config.userCount, 0);

		const rowActions = [
			{
				label: ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_ACTION_EDIT,
				action: this.props.OpenEditConfigModal,
			},
		];
		if (!this.props.isSurveyAdmin) {
			rowActions.push({
				label: ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_ACTION_DELETE,
				action: this.props.OpenDeleteConfigModal,
			});
		}

		return (
			<>
				<Col10Col2
					id={'tws-reporting-users-bulk'}
					leftContent={
						<>
							<Label size='md'>
								<LabelTextWithCounter>{ContentConstants.SUBSECTIONHEADERS_REPORTING_REPORTING_USERS_BULK}</LabelTextWithCounter>
								&nbsp;
								<LabelTextWithCounter>
									<CounterPill size='sm' actualCount={totalUsers} />
								</LabelTextWithCounter>
							</Label>
							<div id={'tws-reporting-user-bulk-description'}>{ParseHtml(ContentConstants.SECTION_DESCRIPTION_REPORTING_USERS_BULK)}</div>
						</>
					}
					rightContent={
						<>
							<Button
								size={'sm'}
								color={'secondary'}
								disabled={this.GroupAccessInfoNotAvailable}
								onClick={() =>
									this.props.OpenEditConfigModal({
										groupAccessProfiles: [],
										comparisonGroupTypes: GroupType.None,
										scopeGroupTypes: GroupType.None,
										userCount: 0,
										hierarchyName: '',
									})
								}
							>
								<Icon name='add' />
								{ContentConstants.BUTTONS_ADD_USERS}
							</Button>
						</>
					}
				/>
				{Object.entries(this.Warnings).map(([key, message]) => (
					<div key={key}>
						<br />
						<Pullout type={PulloutType.Warning}>{ParseHtml(message)}</Pullout>
					</div>
				))}
				{this.props.hierarchyReportingAccessConfigs.length > 0 && hrisId && !this.GroupAccessInfoNotAvailable && (
					<>
						<br />
						<ExpandableRowTable className={'tws-hierarchy-reporting-access-config-table'} lastalign={'right'}>
							<thead>
								<tr>
									<th />
									<th>{ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_HEADER_HIERARCHY}</th>
									<th>{ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_HEADER_REPORTING_USERS_COUNT}</th>
									<th>{ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_HEADER_PROFILES}</th>
									<th>{ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_HEADER_SCOPEGROUPS}</th>
									<th>{ContentConstants.HIERARCHY_REPORTING_ACCESS_TABLE_HEADER_ACTIONS}</th>
								</tr>
							</thead>
							<tbody>
								{this.props.hierarchyReportingAccessConfigs.map((c) => {
									return <HierarchyReportingAccessConfigRow key={c.hierarchyName} hrisId={hrisId} config={c} actionOptions={rowActions} />;
								})}
							</tbody>
						</ExpandableRowTable>
					</>
				)}
				<HierarchyReportingAccessConfigModalStoreWrapper
					{...this.props}
					initialModel={this.props.editConfig}
					isOpen={this.props.showEditConfigModal}
					onClosed={this.props.CloseEditConfigModal}
					onUpdate={this.UpdateHierarchyReportingAccessConfig}
					onAdd={this.AddHierarchyReportingAccessConfig}
					editPersonSelectionEnabled={!this.props.isSurveyAdmin}
				/>
				{this.props.editConfig && (
					<HierarchyReportingAccessDeleteModal
						initialModel={this.props.editConfig}
						isOpen={this.props.showDeleteConfigModal}
						onDelete={this.DeleteHierarchyReportingAccessConfig}
						onClosed={this.props.CloseDeleteConfigModal}
					/>
				)}
			</>
		);
	}

	private AddHierarchyReportingAccessConfig(model: IHierarchyReportingAccessConfig) {
		this.props.AddHierarchyReportingAccessConfig(this.props.surveyId, model);
	}

	private DeleteHierarchyReportingAccessConfig(model: IHierarchyReportingAccessConfig) {
		this.props.DeleteHierarchyReportingAccessConfig(this.props.surveyId, model);
	}

	private UpdateHierarchyReportingAccessConfig(model: IHierarchyReportingAccessConfig, closeModal: boolean) {
		this.props.UpdateHierarchyReportingAccessConfig(this.props.surveyId, model, closeModal);
	}
}
